import { ValidEmail } from '@/lib/validations'
import { usePartnerPageDataStore } from '@/store/store'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined'
import { PROFILE_SERVICE_ERRORS } from 'api/constants'
import { useAuth } from 'context/JWTContext'
import { useUpdateProfile } from 'hooks/useUpdateProfile'
import React from 'react'
import { toast } from 'react-hot-toast'

interface ChangeEmailModalProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  onSuccessfullEmailChange?: () => void
}

export function ChangeEmailModal({
  isOpen,
  setIsOpen,
  onSuccessfullEmailChange,
}: ChangeEmailModalProps) {
  const { reloadUser } = useAuth()
  const [email, setEmail] = React.useState<string>('')
  const [page] = usePartnerPageDataStore((state) => [state.partnerPageData])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mutate, isLoading } = useUpdateProfile({ email })

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault()
    mutate(undefined, {
      onSuccess: () => {
        reloadUser()
        localStorage.removeItem('timeLeft')
        localStorage.removeItem('isTimerRunning')
        toast.success('Email has been updated successfully', {
          position: 'top-center',
          style: { background: '#00BFA6', color: '#fff' },
        })
        setIsOpen(false)
        if (onSuccessfullEmailChange) {
          onSuccessfullEmailChange()
        }
      },
      onError: (e: any) =>
        toast.error(
          PROFILE_SERVICE_ERRORS[e.response.data.detail as keyof typeof PROFILE_SERVICE_ERRORS],
        ) as unknown as void,
    })
  }

  const validatedEmail = ValidEmail.safeParse(email)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEmail(value)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <Modal
      open={isOpen}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: isMobile ? '10%' : 'none',
        justifyContent: isMobile ? 'flex-start' : 'center',
        alignItems: 'center',
      }}
    >
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        sx={{
          p: 3,
          position: 'relative',
          borderRadius: '8px',
          gap: 2,
          width: isMobile ? '95vw' : '500px',
          backgroundColor: 'rgba(245, 245, 245, 1)',
        }}
      >
        <Box
          sx={{
            width: '30px',
            height: '30px',
            background: 'linear-gradient(180deg, #FFE600 0%, #D8AF1E 100%)',
            borderRadius: '50%',
            position: 'absolute',
            top: '-15px',
            left: 'calc(50% - 15px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DraftsOutlinedIcon sx={{ color: 'rgba(53, 53, 53, 1)' }} fontSize="small" />
        </Box>
        <IconButton
          onClick={() => setIsOpen(false)}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            textAlign: 'center',
            fontFamily: 'Montserrat',
            fontSize: '18px',
            color: page?.primary_button_color,
            fontWeight: 'bold',
            fontStyle: 'italic',
          }}
        >
          CHANGE EMAIL
        </Typography>

        {onSuccessfullEmailChange && (
          <Typography
            sx={{
              fontSize: '12px',
              color: 'rgba(53, 53, 53, 1)',
              textAlign: 'center',
              width: '90%',
            }}
          >
            Enter a different email address to send your verification email to
          </Typography>
        )}
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            onChange={handleChange}
            value={email}
            type="email"
            variant="outlined"
            sx={{
              borderRadius: '8px',
              width: '100%',
              mb: 3,
              mt: 2,
              backgroundColor: 'white',
            }}
            InputProps={{
              style: {
                borderRadius: '8px',
                border: '1px solid #DDE6F1',
              },
            }}
          />
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            sx={{ gap: 2 }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ textTransform: 'none', height: '50px' }}
              disabled={!email.length || isLoading || !validatedEmail.success}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Submit'}
            </Button>

            <Typography
              sx={{
                color: 'rgba(154, 154, 154, 1)',
                cursor: 'pointer',
                textAlign: 'center',
                textDecoration: 'underline',
              }}
              onClick={handleClose}
            >
              Cancel
            </Typography>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}
